/**
 * ficheSeanceMapping.js
 *
 * Pont entre les 27 études de cas CCS 2R (`CAS_DETAILS`) et les canevas
 * pédagogiques officiels (`TRAME_SALLE` / `TRAME_CONDUITE` dans `canevasMeta`).
 *
 * Idée centrale : un cas CCS contient déjà tout ce qu'il faut pour produire
 * la fiche de séance attendue par le jury :
 *   - publicAttendu, leviers/freins   → accueil / représentations
 *   - objectifs (connaitre/comprendre/agir) → annonce des objectifs / critères
 *   - methodesAttendues / supportsAttendus  → cours / démo / guidage
 *   - evaluationAttendue              → vérification / éval. finale
 *   - pointsCles                      → synthèse / bilan
 *
 * Ce module expose :
 *   - buildFicheReponses(cas, trameId)
 *       → renvoie un dictionnaire { stepId: { title, content, meta } }
 *         qui représente la fiche de séance "complète" attendue pour ce cas.
 *   - buildFicheQuiz(cas, trameId)
 *       → renvoie un tableau de questions (1 par step) alignées sur la fiche,
 *         avec 1 bonne réponse + distracteurs réalistes tirés d'autres cas.
 *
 * Le quiz généré est calibré pour "remplir" la fiche au fil des bonnes
 * réponses (mode auto-fill / projection live).
 */

import { CAS_DETAILS } from '../ccs2r/casDetails';
import { TRAME_SALLE, TRAME_CONDUITE, getTrameById } from './canevasMeta';

/* ─── Utils internes ──────────────────────────────────────────────────── */

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickDistinctFromOtherCases(currentCasNum, getValue, count = 3) {
  const pool = CAS_DETAILS
    .filter((c) => c.num !== currentCasNum)
    .map((c) => {
      try {
        return getValue(c);
      } catch {
        return null;
      }
    })
    .filter((v) => typeof v === 'string' && v.trim().length > 0);

  const unique = Array.from(new Set(pool));
  return shuffle(unique).slice(0, count);
}

function clip(text, max = 180) {
  if (typeof text !== 'string') return '';
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/* ─── Génération des contenus attendus de la fiche ────────────────────── */

/**
 * Pour TRAME_SALLE — 7 étapes.
 */
function buildFicheReponsesSalle(cas) {
  return {
    accueil: {
      title: 'Accueil & mise en situation',
      content: `Accueillir : ${cas.publicAttendu}`,
      meta: 'Tour de table — vérifier les pré-requis',
    },
    objectif: {
      title: 'Annonce des objectifs',
      content: cas.objectifs?.agir || cas.objectifs?.comprendre || '',
      meta: `À la fin de la séance, l'élève sera capable de…`,
    },
    representations: {
      title: 'Recueil des représentations',
      content: `Freins identifiés : ${(cas.freinsAttendus || []).slice(0, 3).join(' · ')}`,
      meta: `Leviers : ${(cas.leviersAttendus || []).slice(0, 3).join(' · ')}`,
    },
    cours: {
      title: 'Apports pédagogiques (cours)',
      content: `Méthodes : ${(cas.methodesAttendues || []).join(', ')}`,
      meta: `Supports : ${(cas.supportsAttendus || []).slice(0, 3).join(' · ')}`,
    },
    verification: {
      title: 'Vérification des acquis',
      content: 'QCM intermédiaire à main levée (3-5 questions) sur les notions clés',
      meta: 'Évaluation formative — ajustement en direct',
    },
    synthese: {
      title: 'Synthèse',
      content: `Points-clés : ${(cas.pointsCles || []).slice(0, 4).join(' · ')}`,
      meta: '3 blocs : à retenir / pièges / bonnes pratiques',
    },
    evaluation: {
      title: 'Évaluation finale',
      content: cas.evaluationAttendue || '',
      meta: 'Évaluation sommative — acquis / en cours / non acquis',
    },
  };
}

/**
 * Pour TRAME_CONDUITE — 9 étapes (4 phases).
 * Les cas CCS sont avant tout théoriques, mais on peut les transposer en
 * leçon de conduite courte centrée sur le geste/cas.
 */
function buildFicheReponsesConduite(cas) {
  return {
    evalStatique: {
      title: 'Évaluation statique',
      content: `Questionner sur le contexte : ${clip(cas.publicAttendu, 120)}`,
      meta: 'L\'élève parle, le moniteur écoute',
    },
    evalDynamique: {
      title: 'Évaluation dynamique',
      content: 'Faire conduire en situation courte + observer regard/allure/trajectoire/priorités',
      meta: 'Constat factuel — point de départ de la leçon',
    },
    objectif: {
      title: "Annonce de l'objectif",
      content: cas.objectifs?.agir || '',
      meta: 'Phrase observable et mesurable',
    },
    criteres: {
      title: 'Critères de réussite',
      content: `3 à 5 critères observables tirés des points-clés : ${(cas.pointsCles || []).slice(0, 3).join(' · ')}`,
      meta: 'Fil rouge de la séance + base d\'auto-évaluation',
    },
    cours: {
      title: 'Cours (apports)',
      content: `Méthodes mobilisées : ${(cas.methodesAttendues || []).join(', ')}`,
      meta: `Supports : ${(cas.supportsAttendus || []).slice(0, 3).join(' · ')}`,
    },
    demonstration: {
      title: 'Démonstration',
      content: 'Démo commentée par le moniteur, puis démo commentée guidée',
      meta: 'SCHÉMA → DÉMO → ESSAIS (toujours dans cet ordre)',
    },
    guidage: {
      title: 'Guidage dégressif',
      content: 'Verbal FORT → MOYEN → FAIBLE → gestuel uniquement',
      meta: 'Mesure clé pour le jury — capacité à lâcher progressivement',
    },
    autonomie: {
      title: 'Autonomie',
      content: '3 à 5 passages consécutifs sans intervention — observation des critères',
      meta: 'Preuve de l\'autonomie acquise',
    },
    bilan: {
      title: 'Bilan final',
      content: `Auto-évaluation D'ABORD, puis retour moniteur (2 réussites + 1 axe). Suite : ${clip(cas.evaluationAttendue, 120)}`,
      meta: 'Inscription au livret REMC : sous-compétence validée ou non',
    },
  };
}

export function buildFicheReponses(cas, trameId = 'salle') {
  if (!cas) return {};
  if (trameId === 'conduite') return buildFicheReponsesConduite(cas);
  return buildFicheReponsesSalle(cas);
}

/* ─── Génération du QCM aligné étape par étape ────────────────────────── */

/**
 * Pour chaque step, on construit une question dont la BONNE réponse est
 * directement la valeur attendue pour ce step (ou un proxy court), et les
 * distracteurs sont des valeurs équivalentes prises sur d'autres cas.
 *
 * Format question :
 *   {
 *     stepId: 'accueil',
 *     stepLabel: 'Accueil & mise en situation',
 *     prompt: 'Pour ce cas, à qui s\'adresse la séance ?',
 *     fieldHint: 'public' | 'objectif' | 'evaluation' | ...
 *     options: [{ label, correct, fillValue? }, ...]
 *     correctIdx: number,
 *     fillTarget: { stepId, field }  // où placer la valeur dans la fiche
 *     fillValue: string                // valeur à inscrire dans la fiche
 *   }
 */

function buildQuestion({
  stepId, stepLabel, prompt, correctValue, distractors, fillField = 'content',
}) {
  if (!correctValue || !correctValue.trim()) return null;
  const opts = [
    { label: correctValue, correct: true },
    ...distractors.filter(Boolean).slice(0, 3).map((d) => ({ label: d, correct: false })),
  ];
  // Toujours 4 options : compléter si besoin
  while (opts.length < 4) {
    opts.push({ label: 'Aucune des propositions ci-dessus', correct: false });
  }
  const shuffled = shuffle(opts);
  const correctIdx = shuffled.findIndex((o) => o.correct);
  return {
    stepId,
    stepLabel,
    prompt,
    options: shuffled,
    correctIdx,
    fillTarget: { stepId, field: fillField },
    fillValue: correctValue,
  };
}

function buildQuizSalle(cas) {
  const reponses = buildFicheReponsesSalle(cas);

  const questions = [];

  // 1. Accueil — public attendu
  questions.push(buildQuestion({
    stepId: 'accueil',
    stepLabel: 'Accueil & mise en situation',
    prompt: 'À qui s\'adresse cette séance (public attendu) ?',
    correctValue: cas.publicAttendu,
    distractors: pickDistinctFromOtherCases(cas.num, (c) => c.publicAttendu, 3),
  }));

  // 2. Objectif — objectif "agir"
  questions.push(buildQuestion({
    stepId: 'objectif',
    stepLabel: 'Annonce des objectifs',
    prompt: 'Quel est l\'objectif "Agir" attendu en fin de séance ?',
    correctValue: cas.objectifs?.agir,
    distractors: pickDistinctFromOtherCases(cas.num, (c) => c.objectifs?.agir, 3),
  }));

  // 3. Représentations — frein dominant
  const freinPrincipal = (cas.freinsAttendus || [])[0];
  questions.push(buildQuestion({
    stepId: 'representations',
    stepLabel: 'Recueil des représentations',
    prompt: 'Quel frein principal faut-il faire émerger chez les élèves ?',
    correctValue: freinPrincipal,
    distractors: pickDistinctFromOtherCases(cas.num, (c) => (c.freinsAttendus || [])[0], 3),
  }));

  // 4. Cours — méthodes mobilisées
  const methodesStr = (cas.methodesAttendues || []).join(' · ');
  questions.push(buildQuestion({
    stepId: 'cours',
    stepLabel: 'Apports pédagogiques (cours)',
    prompt: 'Quelles méthodes pédagogiques mobiliser pour ce cas ?',
    correctValue: methodesStr,
    distractors: pickDistinctFromOtherCases(cas.num, (c) => (c.methodesAttendues || []).join(' · '), 3),
  }));

  // 5. Vérification — type d'évaluation formative
  questions.push(buildQuestion({
    stepId: 'verification',
    stepLabel: 'Vérification des acquis',
    prompt: 'Quel type de vérification formative en cours de séance ?',
    correctValue: 'QCM intermédiaire à main levée (3-5 questions) sur les notions clés',
    distractors: [
      'Quiz final noté individuellement',
      'Cas pratique évalué sur la sortie circulation',
      'Examen blanc complet de 45 minutes',
    ],
  }));

  // 6. Synthèse — points clés
  const pointsStr = (cas.pointsCles || []).slice(0, 4).join(' · ');
  questions.push(buildQuestion({
    stepId: 'synthese',
    stepLabel: 'Synthèse',
    prompt: 'Quels sont les points-clés à reformuler en synthèse ?',
    correctValue: pointsStr,
    distractors: pickDistinctFromOtherCases(cas.num, (c) => (c.pointsCles || []).slice(0, 4).join(' · '), 3),
  }));

  // 7. Évaluation finale
  questions.push(buildQuestion({
    stepId: 'evaluation',
    stepLabel: 'Évaluation finale',
    prompt: 'Quelle évaluation finale (sommative) retenir pour ce cas ?',
    correctValue: cas.evaluationAttendue,
    distractors: pickDistinctFromOtherCases(cas.num, (c) => c.evaluationAttendue, 3),
  }));

  return { questions: questions.filter(Boolean), reponses };
}

function buildQuizConduite(cas) {
  const reponses = buildFicheReponsesConduite(cas);
  const questions = [];

  // Conduite : on garde une couverture des 9 steps mais on utilise les
  // données du cas pour les steps "stratégiques" (objectif, critères, cours,
  // bilan) et des bonnes pratiques REMC génériques pour les autres.

  questions.push(buildQuestion({
    stepId: 'evalStatique',
    stepLabel: 'Évaluation statique',
    prompt: 'Comment ouvrir la leçon (phase évaluation statique) ?',
    correctValue: 'Questionner l\'élève avant de monter en moto : dernière leçon, difficultés, état d\'esprit',
    distractors: [
      'Démarrer directement la démo sur le plateau',
      'Donner immédiatement l\'objectif sans dialogue',
      'Faire passer un QCM écrit de 10 questions',
    ],
  }));

  questions.push(buildQuestion({
    stepId: 'evalDynamique',
    stepLabel: 'Évaluation dynamique',
    prompt: 'Que faut-il observer pendant l\'évaluation dynamique ?',
    correctValue: 'Regard porté loin, allure adaptée, trajectoire fluide, priorités respectées',
    distractors: [
      'Uniquement le respect des limitations de vitesse',
      'Seulement la position du corps sur la moto',
      'Le port de l\'équipement obligatoire',
    ],
  }));

  questions.push(buildQuestion({
    stepId: 'objectif',
    stepLabel: "Annonce de l'objectif",
    prompt: 'Quel objectif "Agir" annoncer pour cette leçon ?',
    correctValue: cas.objectifs?.agir,
    distractors: pickDistinctFromOtherCases(cas.num, (c) => c.objectifs?.agir, 3),
  }));

  questions.push(buildQuestion({
    stepId: 'criteres',
    stepLabel: 'Critères de réussite',
    prompt: 'Sur quoi appuyer les critères de réussite observables ?',
    correctValue: (cas.pointsCles || []).slice(0, 3).join(' · '),
    distractors: pickDistinctFromOtherCases(cas.num, (c) => (c.pointsCles || []).slice(0, 3).join(' · '), 3),
  }));

  questions.push(buildQuestion({
    stepId: 'cours',
    stepLabel: 'Cours (apports)',
    prompt: 'Quels supports mobiliser dans la phase apports ?',
    correctValue: (cas.supportsAttendus || []).join(' · '),
    distractors: pickDistinctFromOtherCases(cas.num, (c) => (c.supportsAttendus || []).join(' · '), 3),
  }));

  questions.push(buildQuestion({
    stepId: 'demonstration',
    stepLabel: 'Démonstration',
    prompt: 'Quel est l\'ordre attendu par le jury pour la démonstration ?',
    correctValue: 'SCHÉMA → DÉMO commentée → ESSAIS guidés',
    distractors: [
      'ESSAIS → SCHÉMA → DÉMO',
      'DÉMO silencieuse → SCHÉMA → ESSAIS',
      'SCHÉMA uniquement, sans démo',
    ],
  }));

  questions.push(buildQuestion({
    stepId: 'guidage',
    stepLabel: 'Guidage dégressif',
    prompt: 'Quelle est la progression attendue du guidage ?',
    correctValue: 'Verbal FORT → MOYEN → FAIBLE → gestuel uniquement → autonomie',
    distractors: [
      'Guidage verbal constant tout au long de la leçon',
      'Aucun guidage : l\'élève apprend par lui-même',
      'Gestuel uniquement dès le début de la leçon',
    ],
  }));

  questions.push(buildQuestion({
    stepId: 'autonomie',
    stepLabel: 'Autonomie',
    prompt: 'Comment prouver l\'autonomie acquise ?',
    correctValue: '3 à 5 passages consécutifs sans aucune intervention du moniteur',
    distractors: [
      '1 passage réussi suffit à valider l\'autonomie',
      'Observer 10 passages avec corrections gestuelles',
      'L\'élève s\'auto-déclare autonome verbalement',
    ],
  }));

  questions.push(buildQuestion({
    stepId: 'bilan',
    stepLabel: 'Bilan final',
    prompt: 'Qui parle en premier dans le bilan final ?',
    correctValue: 'L\'élève : auto-évaluation D\'ABORD, puis retour moniteur, puis suite',
    distractors: [
      'Le moniteur donne directement sa note finale',
      'L\'élève signe le livret sans discussion',
      'On passe immédiatement à la leçon suivante',
    ],
  }));

  return { questions: questions.filter(Boolean), reponses };
}

export function buildFicheQuiz(cas, trameId = 'salle') {
  if (!cas) return { questions: [], reponses: {} };
  if (trameId === 'conduite') return buildQuizConduite(cas);
  return buildQuizSalle(cas);
}

/* ─── Helpers exposés ─────────────────────────────────────────────────── */

export function getStepsForTrame(trameId) {
  return getTrameById(trameId)?.steps || [];
}

export function getTrameMeta(trameId) {
  if (trameId === 'conduite') return TRAME_CONDUITE;
  return TRAME_SALLE;
}

export function getCasByNum(num) {
  return CAS_DETAILS.find((c) => c.num === Number(num)) || null;
}

export const ALL_CAS = [...CAS_DETAILS].sort((a, b) => a.num - b.num);
